/**
 * Calcola l'altezza di una riga label/valore con testo wrappato.
 * @param {number} labelLineCount
 * @param {number} valueLineCount
 * @param {number} lineHeight
 * @returns {number}
 */
export function computeKeyValueRowHeight(
  labelLineCount,
  valueLineCount,
  lineHeight
) {
  const lines = Math.max(labelLineCount, valueLineCount, 1);
  return lines * lineHeight;
}

/**
 * Layout compiler per jsPDF: cursore Y cumulativo, page break e riserva footer.
 * @param {import('jspdf').jsPDF} doc
 * @param {{ margin?: number, lineHeight?: number, footerHeight?: number, startY?: number }} [options]
 */
export function createPdfLayoutCompiler(doc, options = {}) {
  const margin = options.margin ?? 40;
  const lineHeight = options.lineHeight ?? 20;
  const footerHeight = options.footerHeight ?? 50;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentBottom = pageHeight - footerHeight;

  let y = options.startY ?? margin;

  const getY = () => y;
  const getPageCount = () => doc.internal.getNumberOfPages();

  /**
   * @param {number} requiredHeight
   * @returns {boolean} true se è stata aggiunta una nuova pagina
   */
  function ensureSpace(requiredHeight) {
    if (y + requiredHeight > contentBottom) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  }

  function advance(delta) {
    y += delta;
  }

  function setY(nextY) {
    y = nextY;
  }

  /**
   * @param {string} label
   * @param {string|number|null|undefined} value
   * @param {{ labelOffset?: number, rowGap?: number }} [rowOptions]
   */
  function addKeyValueRow(label, value, rowOptions = {}) {
    const labelOffset = rowOptions.labelOffset ?? 140;
    const rowGap = rowOptions.rowGap ?? 0;
    const labelText = `${label}:`;
    const valueText = String(value ?? '');
    const valueMaxWidth = pageWidth - margin * 2 - labelOffset;

    const labelLines = doc.splitTextToSize(labelText, labelOffset - 8);
    const valueLines = doc.splitTextToSize(valueText, valueMaxWidth);
    const rowHeight = computeKeyValueRowHeight(
      labelLines.length,
      valueLines.length,
      lineHeight
    );

    ensureSpace(rowHeight + rowGap);
    const rowY = y;

    labelLines.forEach((line, index) => {
      doc.text(line, margin, rowY + index * lineHeight);
    });
    valueLines.forEach((line, index) => {
      doc.text(line, margin + labelOffset, rowY + index * lineHeight);
    });

    y += rowHeight + rowGap;
  }

  /**
   * @param {string} text
   * @param {{ before?: number, after?: number, fontSize?: number }} [titleOptions]
   */
  function addSectionTitle(text, titleOptions = {}) {
    const { before = 0, after = lineHeight, fontSize } = titleOptions;

    if (before > 0) {
      advance(before);
    }

    if (fontSize) {
      doc.setFontSize(fontSize);
    }

    ensureSpace(lineHeight);
    doc.setFont('helvetica', 'bold');
    doc.text(text, margin, y);
    doc.setFont('helvetica', 'normal');
    y += after;
  }

  /**
   * @param {string} text
   * @param {{ indent?: number, maxWidth?: number }} [bulletOptions]
   */
  function addBulletLine(text, bulletOptions = {}) {
    const indent = bulletOptions.indent ?? 10;
    const maxWidth = bulletOptions.maxWidth ?? pageWidth - margin * 2 - indent;
    const lines = doc.splitTextToSize(text, maxWidth);
    const blockHeight = lines.length * lineHeight;

    ensureSpace(blockHeight);
    lines.forEach((line, index) => {
      doc.text(line, margin + indent, y + index * lineHeight);
    });
    y += blockHeight;
  }

  /**
   * @param {string[]} lines
   * @param {{ align?: 'left' | 'center' | 'right', lineHeight?: number }} [centerOptions]
   */
  function addCenteredLines(lines, centerOptions = {}) {
    const align = centerOptions.align ?? 'center';
    const lh = centerOptions.lineHeight ?? lineHeight;

    lines.forEach((line) => {
      ensureSpace(lh);
      doc.text(line, pageWidth / 2, y, { align });
      y += lh;
    });
  }

  /**
   * @param {string} color
   * @param {{ before?: number, after?: number }} [ruleOptions]
   */
  function addHorizontalRule(color, ruleOptions = {}) {
    const before = ruleOptions.before ?? 0;
    const after = ruleOptions.after ?? 30;

    if (before > 0) {
      advance(before);
    }

    ensureSpace(after);
    doc.setDrawColor(color);
    doc.setLineWidth(1);
    doc.line(margin, y, pageWidth - margin, y);
    y += after;
  }

  /**
   * Riserva spazio footer e lo disegna sull'ultima pagina.
   * @param {() => void} renderFn
   */
  function drawFooter(renderFn) {
    ensureSpace(footerHeight);
    renderFn();
  }

  return {
    margin,
    lineHeight,
    footerHeight,
    pageWidth,
    pageHeight,
    contentBottom,
    getY,
    getPageCount,
    ensureSpace,
    advance,
    setY,
    addKeyValueRow,
    addSectionTitle,
    addBulletLine,
    addCenteredLines,
    addHorizontalRule,
    drawFooter,
  };
}

export default createPdfLayoutCompiler;
